/**
 * Sales Funnel Component - Visual pipeline representation
 */
import { X } from "lucide-react";

// Funnel stage configuration
const FUNNEL_STAGES = [
  { key: "new", label: "Nove", color: "bg-blue-500", hoverColor: "hover:bg-blue-600", textColor: "text-blue-600", lightBg: "bg-blue-50" },
  { key: "overdue", label: "Zakašnjelo", color: "bg-red-500", hoverColor: "hover:bg-red-600", textColor: "text-red-600", lightBg: "bg-red-50" },
  { key: "today", label: "Danas", color: "bg-green-500", hoverColor: "hover:bg-green-600", textColor: "text-green-600", lightBg: "bg-green-50" },
  { key: "scheduled", label: "Zakazano", color: "bg-purple-500", hoverColor: "hover:bg-purple-600", textColor: "text-purple-600", lightBg: "bg-purple-50" },
  { key: "no_next", label: "Bez datuma", color: "bg-gray-400", hoverColor: "hover:bg-gray-500", textColor: "text-gray-600", lightBg: "bg-gray-50" },
  { key: "acquisition", label: "Akvizicija", color: "bg-emerald-500", hoverColor: "hover:bg-emerald-600", textColor: "text-emerald-600", lightBg: "bg-emerald-50" },
  { key: "not_interested", label: "Nije zaint.", color: "bg-orange-500", hoverColor: "hover:bg-orange-600", textColor: "text-orange-600", lightBg: "bg-orange-50" },
];

export const SalesFunnel = ({ funnelData, loading, onStatusClick, activeStatus }) => {
  const total = funnelData?.total || 0;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-4 animate-fadeIn">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">Sales Pipeline (ukupno: {loading ? "..." : total})</h3>
        {activeStatus && (
          <button 
            onClick={() => onStatusClick(null)}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <X size={12} /> Poništi filter
          </button>
        )}
      </div>
      
      {/* Funnel Bar */}
      <div className="flex gap-1 h-8 rounded-lg overflow-hidden mb-3">
        {FUNNEL_STAGES.map((stage) => {
          const count = funnelData?.[stage.key] || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;
          const isActive = activeStatus === stage.key;
          
          if (count === 0) return null;
          
          return (
            <button
              key={stage.key}
              onClick={() => onStatusClick(stage.key)}
              className={`${stage.color} ${stage.hoverColor} transition-all relative group ${isActive ? 'ring-2 ring-offset-1 ring-gray-800' : ''}`}
              style={{ width: `${Math.max(percentage, 8)}%` }}
              title={`${stage.label}: ${count}`}
            >
              <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                {count}
              </span>
            </button>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {FUNNEL_STAGES.map((stage) => {
          const count = funnelData?.[stage.key] || 0;
          const isActive = activeStatus === stage.key;
          
          return (
            <button
              key={stage.key}
              onClick={() => onStatusClick(stage.key)}
              className={`flex items-center gap-1.5 text-xs transition-all rounded px-1.5 py-0.5 ${isActive ? stage.lightBg + ' font-medium' : 'hover:bg-gray-50'}`}
            >
              <span className={`w-2 h-2 rounded-full ${stage.color}`}></span>
              <span className={isActive ? stage.textColor : 'text-gray-600'}>{stage.label}</span>
              <span className={`font-medium ${isActive ? stage.textColor : 'text-gray-900'}`}>{loading ? "-" : count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Status map for filtering companies via the funnel.
//
// Two distinct filter spaces exist:
//   1. Legacy Home-page Sales Pipeline → computed_status codes (NEW, OVERDUE…)
//   2. Dashboard SalesFunnelCard       → funnel_stage keys (sastanak_odrzan…)
//
// Backend `/companies/queue?status_filter=…` knows both and routes each value
// to the correct field. We therefore just pass the funnel_stage key verbatim
// for the new stages (no lossy coercion to computed_status anymore).
export const FUNNEL_STATUS_MAP = {
  // Legacy Home-page Sales Pipeline (computed_status semantics)
  'new': 'NEW',
  'overdue': 'OVERDUE',
  'today': 'TODAY',
  'scheduled': ['SCHEDULED', 'MEETING_SCHEDULED'],
  'no_next': ['NO_NEXT', 'FOLLOW_UP'],
  'acquisition': 'ACQUISITION',
  'not_interested': ['NOT_INTERESTED', 'DO_NOT_CONTACT'],
  // Dashboard SalesFunnelCard (true funnel_stage semantics — pass through)
  'nova_firma': 'nova_firma',
  'u_kontaktu': 'u_kontaktu',
  'sastanak_zakazan': 'sastanak_zakazan',
  'sastanak_odrzan': 'sastanak_odrzan',
  'ponuda_poslana': 'ponuda_poslana',
  'zavrsni_followup': 'zavrsni_followup',
  'akvizicija': 'akvizicija',
  'nije_zainteresiran': 'nije_zainteresiran',
  'ne_kontaktirati': 'ne_kontaktirati',
};
